<template>
  <div class="quality-page">
    <PageHeader icon="fas fa-shield-alt"
      ><template #title>Mutu, Akreditasi & Keselamatan</template
      ><template #subtitle
        >Pusat kendali kesiapan akreditasi, RME, indikator, insiden, risiko,
        PPI, SOP, kalibrasi, evidence, dan CAPA.</template
      ><template #actions
        ><button
          v-if="
            editableTabs.includes(tab) &&
            (canManage || (canSafety && ['incidents', 'capa'].includes(tab)))
          "
          class="primary"
          @click="open(tab)"
        >
          <i class="fas fa-plus"></i> Tambah
        </button></template
      ></PageHeader
    >
    <div class="kpis">
      <article>
        <small>Kesiapan akreditasi</small
        ><b>{{ accSummary.readiness_score || 0 }}%</b>
      </article>
      <article>
        <small>Kelengkapan RME</small><b>{{ rmeSummary.average || 0 }}%</b>
      </article>
      <article>
        <small>Kepatuhan SOAP</small
        ><b>{{ rmeSummary.soap_compliance || 0 }}%</b>
      </article>
      <article>
        <small>Capaian indikator</small
        ><b>{{ summary.indicator_achievement || 0 }}%</b>
      </article>
      <article>
        <small>CAPA terbuka</small><b>{{ summary.open_actions || 0 }}</b>
      </article>
      <article>
        <small>Insiden bulan ini</small
        ><b>{{ summary.incidents_month || 0 }}</b>
      </article>
    </div>
    <nav>
      <button
        v-for="t in tabs"
        :key="t.id"
        :class="{ active: tab === t.id }"
        @click="select(t.id)"
      >
        <i :class="t.icon"></i>{{ t.label }}
      </button>
    </nav>
    <section v-if="tab === 'dashboard'" class="grid">
      <Panel title="CAPA mendekati tenggat" eyebrow="PRIORITAS"
        ><div class="rows">
          <p v-for="x in urgent" :key="x.id">
            <b>{{ x.action }}</b
            ><small
              >{{ label(x.source_type) }} #{{ x.source_id }} ·
              {{ date(x.due_date) }}</small
            >
          </p>
          <em v-if="!urgent.length">Tidak ada tindak lanjut mendesak.</em>
        </div></Panel
      ><Panel title="STR/SIP perlu perhatian" eyebrow="KREDENTIAL"
        ><div class="rows">
          <p
            v-for="x in credentials
              .filter((y) => y.alert !== 'valid')
              .slice(0, 8)"
            :key="x.id"
          >
            <b>{{ x.name }}</b
            ><small
              >{{ label(x.alert) }} · STR {{ date(x.str_expired_at) }} · SIP
              {{ date(x.sip_expired_at) }}</small
            >
          </p>
          <em v-if="!credentials.filter((y) => y.alert !== 'valid').length"
            >Tidak ada izin yang perlu perhatian.</em
          >
        </div></Panel
      >
    </section>
    <Panel
      v-else-if="tab === 'accreditation'"
      title="Checklist kesiapan akreditasi"
      eyebrow="READINESS"
      ><DataTable :rows="accreditation" :cols="cols.accreditation"
        ><template #action="{ row }"
          ><button v-if="canManage" @click="editAssessment(row)">
            Nilai
          </button></template
        ></DataTable
      ></Panel
    >
    <Panel
      v-else-if="tab === 'rme'"
      title="Audit kelengkapan RME & SOAP"
      eyebrow="CLINICAL DOCUMENTATION"
      ><DataTable :rows="rme" :cols="cols.rme"
        ><template #action="{ row }"
          ><button v-if="canManage" @click="auditRme(row)">
            Audit
          </button></template
        ></DataTable
      ></Panel
    >
    <Panel
      v-else-if="tab === 'indicators'"
      title="Indikator nasional, prioritas & unit"
      eyebrow="INDIKATOR MUTU"
      ><DataTable :rows="indicators" :cols="cols.indicators"
        ><template #action="{ row }"
          ><button v-if="canManage" @click="measure(row)">
            Input capaian
          </button></template
        ></DataTable
      ></Panel
    >
    <Panel v-else :title="meta[tab].title" :eyebrow="meta[tab].eyebrow"
      ><DataTable :rows="rowsFor(tab)" :cols="cols[tab]"
        ><template #action="{ row }"
          ><button
            v-if="
              canManage &&
              [
                'complaints',
                'risks',
                'ppi',
                'documents',
                'equipments',
              ].includes(tab)
            "
            @click="editGeneric(row)"
          >
            Ubah</button
          ><button
            v-else-if="canSafety && ['incidents', 'capa'].includes(tab)"
            @click="editGeneric(row)"
          >
            Proses
          </button></template
        ></DataTable
      ></Panel
    >
    <b-modal
      ref="modal"
      size="lg"
      hide-footer
      :title="'Input ' + label(resource)"
      ><div class="form-grid">
        <label
          v-for="f in fields(resource)"
          :key="f.key"
          :class="{ wide: f.wide }"
          >{{ f.label
          }}<textarea
            v-if="f.type === 'textarea'"
            v-model="form[f.key]"
            rows="3" /><select v-else-if="f.options" v-model="form[f.key]">
            <option v-for="o in f.options" :key="String(o)" :value="o">
              {{ label(o) }}
            </option></select
          ><clinical-file-upload
            v-else-if="f.type === 'file'"
            v-model="form.file"
            aria-label="Pilih file dokumen mutu"
          ></clinical-file-upload><input
            v-else
            :type="f.type || 'text'"
            v-model="form[f.key]" /></label
        ><label v-if="['evidence', 'documents'].includes(resource)" class="wide"
          >File dokumen<clinical-file-upload
            v-model="form.file"
            aria-label="Pilih file dokumen mutu"
          ></clinical-file-upload></label>
      </div>
      <footer>
        <button @click="$refs.modal.hide()">Batal</button
        ><button class="primary" :disabled="saving" @click="save">
          Simpan
        </button>
      </footer></b-modal
    >
  </div>
</template>
<script>
import ApiService from "@/core/services/api.service";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
const Panel = {
  props: ["title", "eyebrow"],
  template: `<section class="panel"><header><div><small>{{eyebrow}}</small><h3>{{title}}</h3></div><slot name="header"/></header><slot/></section>`,
};
const DataTable = {
  props: ["rows", "cols"],
  methods: {
    show(v) {
      if (v === null || v === undefined || v === "") return "-";
      if (Array.isArray(v)) return v.join(", ");
      return String(v).replaceAll("_", " ");
    },
  },
  template: `<div class="table-wrap"><table><thead><tr><th v-for="c in cols" :key="c.k">{{c.l}}</th><th v-if="$scopedSlots.action"></th></tr></thead><tbody><tr v-for="r in rows" :key="r.id"><td v-for="c in cols" :key="c.k"><span :class="{badge:c.k==='status'||c.k==='alert'}">{{show(r[c.k])}}</span></td><td v-if="$scopedSlots.action"><slot name="action" :row="r"/></td></tr><tr v-if="!rows.length"><td :colspan="cols.length+1" class="empty">Belum ada data.</td></tr></tbody></table></div>`,
};
const C = (...x) => x.map((v) => ({ k: v[0], l: v[1] }));
export default {
  components: { Panel, DataTable },
  data: () => ({
    tab: "dashboard",
    resource: "",
    saving: false,
    form: {},
    summary: {},
    urgent: [],
    accSummary: {},
    rmeSummary: {},
    accreditation: [],
    rme: [],
    indicators: [],
    audits: [],
    incidents: [],
    actions: [],
    credentials: [],
    evidences: [],
    resources: {
      complaints: [],
      risks: [],
      ppi: [],
      documents: [],
      equipments: [],
    },
    editableTabs: [
      "complaints",
      "risks",
      "ppi",
      "documents",
      "equipments",
      "indicators",
      "audits",
      "incidents",
      "capa",
      "evidence",
    ],
    tabs: [
      ["dashboard", "Ringkasan", "chart-pie"],
      ["accreditation", "Akreditasi", "clipboard-check"],
      ["rme", "RME & SOAP", "notes-medical"],
      ["indicators", "Indikator", "bullseye"],
      ["audits", "Audit Internal", "search"],
      ["incidents", "Insiden", "user-shield"],
      ["complaints", "Keluhan", "comment-medical"],
      ["risks", "Risiko", "exclamation-circle"],
      ["ppi", "PPI", "hands-wash"],
      ["documents", "SOP", "file-alt"],
      ["equipments", "Kalibrasi", "tools"],
      ["credentials", "STR/SIP", "id-card"],
      ["capa", "CAPA", "tasks"],
      ["evidence", "Evidence", "folder-open"],
    ].map((x) => ({ id: x[0], label: x[1], icon: "fas fa-" + x[2] })),
    meta: {
      incidents: {
        title: "Insiden keselamatan pasien",
        eyebrow: "PATIENT SAFETY",
      },
      audits: { title: "Program audit internal", eyebrow: "INTERNAL AUDIT" },
      complaints: {
        title: "Keluhan & tindak lanjut",
        eyebrow: "VOICE OF CUSTOMER",
      },
      risks: { title: "Risk register klinik", eyebrow: "CLINICAL RISK" },
      ppi: { title: "Pemantauan PPI", eyebrow: "INFECTION CONTROL" },
      documents: {
        title: "Kebijakan, SOP & instruksi kerja",
        eyebrow: "DOCUMENT CONTROL",
      },
      equipments: {
        title: "Alat & jadwal kalibrasi",
        eyebrow: "EQUIPMENT GOVERNANCE",
      },
      credentials: { title: "Masa berlaku STR & SIP", eyebrow: "KREDENTIAL" },
      capa: { title: "CAPA & efektivitas", eyebrow: "CONTINUAL IMPROVEMENT" },
      evidence: { title: "Repository bukti akreditasi", eyebrow: "EVIDENCE" },
    },
    cols: {
      accreditation: C(
        ["element_code", "Elemen"],
        ["chapter_code", "Bab"],
        ["requirement", "Requirement"],
        ["status", "Status"],
        ["score", "Skor"],
        ["target_date", "Target"]
      ),
      rme: C(
        ["date", "Tanggal"],
        ["patient_name", "Pasien"],
        ["doctor_name", "Dokter"],
        ["score", "Lengkap %"],
        ["soap_complete", "SOAP"],
        ["missing", "Belum lengkap"]
      ),
      indicators: C(
        ["code", "Kode"],
        ["name", "Indikator"],
        ["category", "Kategori"],
        ["target", "Target"],
        ["unit", "Satuan"]
      ),
      incidents: C(
        ["incident_number", "Nomor"],
        ["occurred_at", "Waktu"],
        ["unit_name", "Unit"],
        ["incident_type", "Jenis"],
        ["risk_grade", "Risiko"],
        ["status", "Status"]
      ),
      audits: C(
        ["audit_number", "Nomor"],
        ["standard", "Standar"],
        ["unit_name", "Unit"],
        ["audit_date", "Tanggal"],
        ["score", "Skor"],
        ["status", "Status"]
      ),
      complaints: C(
        ["complaint_number", "Nomor"],
        ["received_at", "Diterima"],
        ["category", "Kategori"],
        ["priority", "Prioritas"],
        ["status", "Status"],
        ["response_due_at", "SLA"]
      ),
      risks: C(
        ["risk_number", "Nomor"],
        ["unit_name", "Unit"],
        ["risk_statement", "Risiko"],
        ["inherent_score", "Inheren"],
        ["residual_score", "Residual"],
        ["status", "Status"]
      ),
      ppi: C(
        ["observation_date", "Tanggal"],
        ["unit_name", "Unit"],
        ["indicator_type", "Indikator"],
        ["numerator", "Numerator"],
        ["denominator", "Denominator"],
        ["result", "Capaian %"]
      ),
      documents: C(
        ["document_number", "Nomor"],
        ["title", "Judul"],
        ["document_type", "Jenis"],
        ["version", "Versi"],
        ["status", "Status"],
        ["review_date", "Review"]
      ),
      equipments: C(
        ["asset_code", "Kode"],
        ["name", "Alat"],
        ["unit_name", "Unit"],
        ["risk_class", "Risiko"],
        ["next_calibration_at", "Kalibrasi"],
        ["status", "Status"]
      ),
      credentials: C(
        ["name", "Nama"],
        ["position_name", "Jabatan"],
        ["str_number", "STR"],
        ["str_expired_at", "Berlaku STR"],
        ["sip_number", "SIP"],
        ["sip_expired_at", "Berlaku SIP"],
        ["alert", "Status"]
      ),
      capa: C(
        ["source_type", "Sumber"],
        ["source_id", "ID"],
        ["action", "Tindakan"],
        ["due_date", "Tenggat"],
        ["status", "Status"],
        ["effectiveness", "Efektivitas"]
      ),
      evidence: C(
        ["title", "Judul"],
        ["reference_type", "Terkait"],
        ["document_number", "Nomor"],
        ["version", "Versi"],
        ["valid_from", "Berlaku"],
        ["expires_at", "Kedaluwarsa"],
        ["original_name", "File"]
      ),
    },
  }),
  computed: {
    codes() {
      try {
        return JSON.parse(
          localStorage.getItem("access_right_display") || "[]"
        ).map((x) => String(x.code || x));
      } catch (e) {
        return [];
      }
    },
    canManage() {
      return this.codes.includes("10202");
    },
    canSafety() {
      return this.codes.includes("10203");
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Mutu & Akreditasi" },
      { title: "Pusat Kendali" },
    ]);
    this.bootstrap();
  },
  methods: {
    async bootstrap() {
      const [d, a, r, c] = await Promise.all([
        ApiService.get("quality/dashboard"),
        ApiService.get("quality/accreditation"),
        ApiService.get("quality/rme-completeness"),
        ApiService.get("quality/credentials"),
      ]);
      this.summary = d.data.summary || {};
      this.urgent = d.data.urgent_actions || [];
      this.accSummary = a.data.summary || {};
      this.accreditation = a.data.data || [];
      this.rmeSummary = r.data.summary || {};
      this.rme = r.data.data || [];
      this.credentials = c.data.data || [];
    },
    rowsFor(t) {
      return this.resources[t] || this[t] || [];
    },
    async select(t) {
      this.tab = t;
      if (t === "dashboard" || t === "credentials") return;
      if (t === "accreditation") {
        const r = await ApiService.get("quality/accreditation");
        this.accSummary = r.data.summary || {};
        this.accreditation = r.data.data || [];
        return;
      }
      if (t === "rme") {
        const r = await ApiService.get("quality/rme-completeness");
        this.rmeSummary = r.data.summary || {};
        this.rme = r.data.data || [];
        return;
      }
      if (t === "indicators") {
        const r = await ApiService.get("quality/indicators");
        this.indicators = r.data || [];
      } else if (t === "incidents") {
        const r = await ApiService.get("quality/incidents");
        this.incidents = r.data.data || [];
      } else if (t === "audits") {
        const r = await ApiService.get("quality/audits");
        this.audits = r.data.data || [];
      } else if (t === "capa") {
        const r = await ApiService.get("quality/actions");
        this.actions = r.data.data || [];
      } else if (t === "evidence") {
        const r = await ApiService.get("quality/evidences");
        this.evidences = r.data.data || [];
      } else {
        const r = await ApiService.get("quality/governance/" + t);
        this.resources[t] = r.data.data || [];
      }
    },
    open(x) {
      this.resource = x;
      this.form = this.defaults(x);
      this.$refs.modal.show();
    },
    editGeneric(row) {
      this.resource = this.tab;
      this.form = { ...row };
      this.$refs.modal.show();
    },
    editAssessment(x) {
      this.resource = "assessment";
      this.form = {
        id: x.id,
        status: x.status || "not_assessed",
        score: x.score || 0,
        target_date: x.target_date || "",
        notes: x.notes || "",
      };
      this.$refs.modal.show();
    },
    auditRme(x) {
      this.resource = "rmeAudit";
      this.form = {
        id: x.id,
        identity_complete: !x.missing.includes("identity"),
        soap_complete: x.soap_complete,
        diagnosis_complete: !x.missing.includes("assessment"),
        authentication_complete: !x.missing.includes("authentication"),
        findings: x.missing.join(", "),
      };
      this.$refs.modal.show();
    },
    measure(x) {
      this.resource = "measurement";
      this.form = {
        id: x.id,
        period_start: this.today(),
        period_end: this.today(),
        numerator: 0,
        denominator: 0,
        result: 0,
        analysis: "",
        follow_up: "",
      };
      this.$refs.modal.show();
    },
    async save() {
      this.saving = true;
      try {
        let p = this.formResourcePath();
        await p;
        this.$refs.modal.hide();
        await this.select(this.afterSaveTab());
        this.$bvToast.toast("Data berhasil disimpan.", {
          title: "Berhasil",
          variant: "success",
          solid: true,
        });
      } catch (e) {
        this.$bvToast.toast(
          (e.response && e.response.data && e.response.data.message) ||
            "Periksa data wajib.",
          { title: "Belum tersimpan", variant: "danger", solid: true }
        );
      } finally {
        this.saving = false;
      }
    },
    formResourcePath() {
      if (this.resource === "assessment")
        return ApiService.put(
          "quality/accreditation/" + this.form.id,
          this.form
        );
      if (this.resource === "rmeAudit")
        return ApiService.post("quality/rme-audits/" + this.form.id, this.form);
      if (this.resource === "measurement")
        return ApiService.post(
          "quality/indicators/" + this.form.id + "/measurements",
          this.form
        );
      if (this.resource === "indicators")
        return ApiService.post("quality/indicators", this.form);
      if (this.resource === "incidents")
        return this.form.id
          ? ApiService.put("quality/incidents/" + this.form.id, this.form)
          : ApiService.post("quality/incidents", this.form);
      if (this.resource === "audits")
        return ApiService.post("quality/audits", this.form);
      if (this.resource === "capa")
        return this.form.id
          ? ApiService.put("quality/actions/" + this.form.id, this.form)
          : ApiService.post("quality/actions", this.form);
      if (this.resource === "evidence")
        return ApiService.post("quality/evidences", this.form);
      return this.form.id
        ? ApiService.put(
            "quality/governance/" + this.resource + "/" + this.form.id,
            this.form
          )
        : ApiService.post("quality/governance/" + this.resource, this.form);
    },
    afterSaveTab() {
      return (
        {
          assessment: "accreditation",
          rmeAudit: "rme",
          measurement: "indicators",
        }[this.resource] || this.resource
      );
    },
    today() {
      return new Date().toISOString().slice(0, 10);
    },
    defaults(x) {
      let now = new Date().toISOString().slice(0, 16);
      return (
        {
          complaints: {
            received_at: now,
            channel: "in_person",
            category: "pelayanan",
            priority: "normal",
            description: "",
            status: "received",
          },
          risks: {
            unit_name: "",
            category: "clinical",
            risk_statement: "",
            likelihood: 1,
            impact: 1,
            residual_likelihood: 1,
            residual_impact: 1,
            status: "open",
          },
          ppi: {
            observation_date: this.today(),
            unit_name: "",
            indicator_type: "hand_hygiene",
            numerator: 0,
            denominator: 0,
          },
          documents: {
            document_number: "",
            title: "",
            document_type: "sop",
            version: "1.0",
            status: "draft",
          },
          equipments: {
            asset_code: "",
            name: "",
            unit_name: "",
            risk_class: "medium",
            calibration_frequency: "annual",
            status: "active",
          },
          indicators: {
            code: "",
            name: "",
            category: "national",
            numerator_definition: "",
            denominator_definition: "",
            target: 100,
            target_operator: "gte",
            unit: "%",
            frequency: "monthly",
            result_type: "percentage",
            official_reference: "",
            data_source: "",
          },
          incidents: {
            occurred_at: now,
            unit_name: "",
            incident_type: "knc",
            risk_grade: "green",
            category: "",
            chronology: "",
          },
          audits: {
            standard: "Akreditasi Klinik",
            chapter: "",
            unit_name: "",
            audit_date: this.today(),
            compliant_count: 0,
            partial_count: 0,
            noncompliant_count: 0,
            findings: "",
            recommendation: "",
            status: "planned",
          },
          capa: {
            source_type: "audit",
            source_id: "",
            action: "",
            due_date: "",
          },
          evidence: {
            reference_type: "accreditation",
            reference_id: "",
            title: "",
            version: "1.0",
          },
        }[x] || {}
      );
    },
    fields(x) {
      let O = (key, label, options) => ({ key, label, options }),
        T = (key, label, wide = false) => ({
          key,
          label,
          type: "textarea",
          wide,
        });
      return (
        {
          assessment: [
            O("status", "Status", [
              "not_assessed",
              "not_ready",
              "partial",
              "ready",
              "not_applicable",
            ]),
            { key: "score", label: "Skor", type: "number" },
            { key: "target_date", label: "Target", type: "date" },
            T("notes", "Catatan", true),
          ],
          rmeAudit: [
            O("identity_complete", "Identitas lengkap", [true, false]),
            O("soap_complete", "SOAP lengkap", [true, false]),
            O("diagnosis_complete", "Diagnosis lengkap", [true, false]),
            O("authentication_complete", "Autentikasi lengkap", [true, false]),
            T("findings", "Temuan", true),
          ],
          measurement: [
            { key: "period_start", label: "Awal", type: "date" },
            { key: "period_end", label: "Akhir", type: "date" },
            { key: "numerator", label: "Numerator", type: "number" },
            { key: "denominator", label: "Denominator", type: "number" },
            { key: "result", label: "Hasil absolut", type: "number" },
            T("analysis", "Analisis", true),
            T("follow_up", "Tindak lanjut", true),
          ],
          indicators: [
            { key: "code", label: "Kode" },
            { key: "name", label: "Nama" },
            O("category", "Kategori", ["national", "priority", "unit"]),
            T("numerator_definition", "Definisi numerator", true),
            T("denominator_definition", "Definisi denominator", true),
            { key: "target", label: "Target", type: "number" },
            O("target_operator", "Operator", ["gte", "lte"]),
            { key: "unit", label: "Satuan" },
            O("frequency", "Frekuensi", ["monthly", "quarterly"]),
            O("result_type", "Jenis hasil", [
              "percentage",
              "absolute",
              "minutes",
              "rate",
            ]),
            { key: "official_reference", label: "Referensi resmi" },
            { key: "data_source", label: "Sumber data" },
          ],
          incidents: [
            { key: "occurred_at", label: "Waktu", type: "datetime-local" },
            { key: "unit_name", label: "Unit" },
            O("incident_type", "Jenis", [
              "kpc",
              "knc",
              "ktc",
              "kti",
              "sentinel",
            ]),
            O("risk_grade", "Risiko", ["blue", "green", "yellow", "red"]),
            { key: "category", label: "Kategori" },
            T("chronology", "Kronologi", true),
            T("immediate_action", "Tindakan segera", true),
            O("status", "Status proses", [
              "reported",
              "verified",
              "investigating",
              "action_plan",
              "monitoring",
              "closed",
            ]),
            O("investigation_method", "Metode investigasi", ["simple", "rca"]),
            T("root_cause", "Akar masalah", true),
            T("recommendation", "Rekomendasi", true),
          ],
          audits: [
            { key: "standard", label: "Standar" },
            { key: "chapter", label: "Bab/area" },
            { key: "unit_name", label: "Unit" },
            { key: "audit_date", label: "Tanggal", type: "date" },
            { key: "compliant_count", label: "Patuh", type: "number" },
            { key: "partial_count", label: "Parsial", type: "number" },
            { key: "noncompliant_count", label: "Tidak patuh", type: "number" },
            T("findings", "Temuan", true),
            T("recommendation", "Rekomendasi", true),
            O("status", "Status", [
              "planned",
              "ongoing",
              "completed",
              "closed",
            ]),
          ],
          capa: [
            O("source_type", "Sumber", ["incident", "audit", "indicator"]),
            { key: "source_id", label: "ID sumber", type: "number" },
            T("action", "Rencana perbaikan", true),
            { key: "due_date", label: "Tenggat", type: "date" },
            O("status", "Status", [
              "open",
              "in_progress",
              "verification",
              "completed",
            ]),
            T("evidence_note", "Catatan evidence", true),
            O("effectiveness", "Efektivitas", [
              "effective",
              "partially_effective",
              "ineffective",
            ]),
            T("verification_note", "Verifikasi efektivitas", true),
          ],
          evidence: [
            O("reference_type", "Terkait", [
              "accreditation",
              "audit",
              "capa",
              "sop",
              "ppi",
              "risk",
              "complaint",
              "calibration",
            ]),
            { key: "reference_id", label: "ID referensi", type: "number" },
            { key: "title", label: "Judul" },
            { key: "document_number", label: "Nomor" },
            { key: "version", label: "Versi" },
            { key: "valid_from", label: "Berlaku", type: "date" },
            { key: "expires_at", label: "Kedaluwarsa", type: "date" },
            T("description", "Deskripsi", true),
          ],
          complaints: [
            { key: "received_at", label: "Diterima", type: "datetime-local" },
            O("channel", "Kanal", [
              "in_person",
              "phone",
              "whatsapp",
              "email",
              "survey",
            ]),
            { key: "category", label: "Kategori" },
            O("priority", "Prioritas", ["low", "normal", "high", "critical"]),
            T("description", "Keluhan", true),
            O("status", "Status", [
              "received",
              "assigned",
              "investigating",
              "responded",
              "resolved",
              "closed",
            ]),
          ],
          risks: [
            { key: "unit_name", label: "Unit" },
            { key: "category", label: "Kategori" },
            T("risk_statement", "Risiko", true),
            { key: "likelihood", label: "Likelihood 1-5", type: "number" },
            { key: "impact", label: "Impact 1-5", type: "number" },
            T("existing_controls", "Kontrol", true),
            {
              key: "residual_likelihood",
              label: "Residual likelihood",
              type: "number",
            },
            {
              key: "residual_impact",
              label: "Residual impact",
              type: "number",
            },
            T("mitigation_plan", "Mitigasi", true),
            { key: "review_date", label: "Review", type: "date" },
            O("status", "Status", ["open", "monitoring", "accepted", "closed"]),
          ],
          ppi: [
            { key: "observation_date", label: "Tanggal", type: "date" },
            { key: "unit_name", label: "Unit" },
            O("indicator_type", "Indikator", [
              "hand_hygiene",
              "apd_compliance",
              "healthcare_infection",
              "needle_stick",
              "waste_management",
              "environmental_hygiene",
            ]),
            { key: "numerator", label: "Numerator", type: "number" },
            { key: "denominator", label: "Denominator", type: "number" },
            T("findings", "Temuan", true),
            T("follow_up", "Tindak lanjut", true),
          ],
          documents: [
            { key: "document_number", label: "Nomor" },
            { key: "title", label: "Judul" },
            O("document_type", "Jenis", [
              "sop",
              "policy",
              "guideline",
              "form",
              "work_instruction",
            ]),
            { key: "unit_name", label: "Unit" },
            { key: "version", label: "Versi" },
            O("status", "Status", [
              "draft",
              "review",
              "approved",
              "effective",
              "obsolete",
            ]),
            { key: "effective_date", label: "Efektif", type: "date" },
            { key: "review_date", label: "Review", type: "date" },
          ],
          equipments: [
            { key: "asset_code", label: "Kode aset" },
            { key: "name", label: "Nama alat" },
            { key: "unit_name", label: "Unit" },
            O("risk_class", "Risiko", ["low", "medium", "high"]),
            { key: "calibration_frequency", label: "Frekuensi" },
            {
              key: "last_calibrated_at",
              label: "Kalibrasi terakhir",
              type: "date",
            },
            {
              key: "next_calibration_at",
              label: "Kalibrasi berikut",
              type: "date",
            },
            O("status", "Status", ["active", "out_of_service", "retired"]),
          ],
        }[x] || []
      );
    },
    date(v) {
      return v ? this.$clinicDate(v) : "-";
    },
    label(v) {
      return String(v === null || v === undefined ? "-" : v)
        .replaceAll("_", " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
  },
};
</script>
<style scoped>
.quality-page {
  padding-bottom: 32px;
}
.kpis {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
}
.kpis article,
.panel {
  border: 1px solid #dfe8ee;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 5px 18px rgba(30, 60, 75, 0.05);
}
.kpis article {
  padding: 14px;
}
.kpis small,
.kpis b {
  display: block;
}
.kpis small {
  color: #77899b;
  font-size: 10px;
}
.kpis b {
  margin-top: 4px;
  color: #183b56;
  font-size: 19px;
}
nav {
  display: flex;
  gap: 4px;
  margin: 16px 0;
  padding: 5px;
  overflow: auto;
  border-radius: 11px;
  background: #eaf0f4;
}
nav button {
  padding: 9px 12px;
  white-space: nowrap;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #5d7083;
  font-size: 11px;
  font-weight: 700;
}
nav button.active {
  background: #fff;
  color: #087f7a;
  box-shadow: 0 2px 8px #ccd8df;
}
nav i {
  margin-right: 6px;
}
.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.panel {
  overflow: hidden;
}
.panel header {
  display: flex;
  justify-content: space-between;
  padding: 15px 17px;
  border-bottom: 1px solid #e6edf2;
}
.panel header small {
  color: #10958e;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.1em;
}
.panel h3 {
  margin: 3px 0 0;
  font-size: 16px;
}
.rows p {
  margin: 0;
  padding: 11px 16px;
  border-top: 1px solid #edf2f5;
}
.rows p:first-child {
  border-top: 0;
}
.rows b,
.rows small {
  display: block;
}
.rows small {
  color: #76889a;
}
.rows em,
.empty {
  display: block;
  padding: 28px;
  color: #8493a3;
  text-align: center;
}
.table-wrap {
  overflow: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th {
  padding: 11px;
  background: #f6f9fb;
  color: #61758a;
  text-align: left;
  text-transform: uppercase;
  font-size: 10px;
}
td {
  padding: 11px;
  border-top: 1px solid #edf2f5;
  color: #34495e;
  font-size: 11px;
}
.badge {
  display: inline-flex;
  padding: 4px 7px;
  border-radius: 6px;
  background: #edf2f6;
  font-weight: 700;
}
.primary,
footer button,
.panel td button {
  padding: 9px 12px;
  border: 1px solid #cfdae4;
  border-radius: 8px;
  background: #fff;
  color: #52677a;
  font-weight: 700;
}
.primary {
  border: 0;
  color: #fff;
  background: linear-gradient(135deg, #145a88, #169b91);
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.form-grid label {
  color: #40566b;
  font-size: 11px;
  font-weight: 700;
}
.form-grid input,
.form-grid select,
.form-grid textarea {
  display: block;
  width: 100%;
  margin-top: 5px;
  padding: 9px;
  border: 1px solid #d4e1e9;
  border-radius: 8px;
}
.form-grid .wide {
  grid-column: 1/-1;
}
footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
@media (max-width: 1100px) {
  .kpis {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (max-width: 700px) {
  .kpis,
  .grid,
  .form-grid {
    grid-template-columns: 1fr;
  }
  .form-grid .wide {
    grid-column: auto;
  }
}
</style>
